// icons from https://www.svgrepo.com/collection/solar-bold-duotone-icons/?search=call

import { ReactNode, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { radius } from '@libertify/ui';
import { CustomTheme, MediaProjectInstanceResponse } from '../types';
import { shouldShowLeadForm } from '../helpers';
import { useExperienceConfig } from '../contexts/ExperienceContext';
import { LeadFormModal } from './LeadFormModal';

type ActionProps = {
  onClick?: () => void;
  publication?: MediaProjectInstanceResponse;
  label: string;
};

const StyledLink = styled.a`
  text-decoration: none;
  padding: 4px;
  overflow: hidden;
  overflow: clip;
  transition: all 0.5s;

  .icon {
    opacity: 0.85;
    transition: all 0.3s;
  }
  &:hover .icon {
    opacity: 1;
    transform: scale(1.1);
  }
  &.has-border {
    background: #f7872733;
  }
  &.has-border:hover {
    background: transparent;
  }
  .wrapper {
    transition: all 0.5s;
    background: var(--primary-color);
    border: 2px solid transparent;
  }
  &:hover .wrapper {
    background: color-mix(in srgb, var(--primary-color) 85%, transparent);
  }

  &.has-desc:hover .title,
  &.has-desc:hover .icon {
    display: none;
  }
  .description {
    transition: all 1s;
    display: none;
    opacity: 0;
    font-size: 16px;
    color: #000;
  }
  &:hover .description {
    transition: opacity 1s;
    display: block;
    opacity: 1;
  }
`;
const StyledLinkBorder = styled.div`
  background-image: linear-gradient(306deg, #1fa464, #000000);
  padding: 0;
  border-radius: ${radius.box}px;
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 1;
  animation: 4s linear infinite animate-rotate;
`;

export const OverlayAction = ({
  children,
  icon,
  color,
  animateBorder,
  description,
  index,
  href,
  download,
  target,
  onClick,
}: {
  children: ReactNode;
  description?: ReactNode;
  icon: ReactNode;
  color?: string;
  animateBorder?: boolean;
  index: number;
  href?: string;
  download?: string;
  target?: React.HTMLAttributeAnchorTarget;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}) => {
  const theme = useTheme() as unknown as CustomTheme;

  return (
    <StyledLink
      onClick={onClick}
      href={href}
      target={target}
      download={download}
      className={`cta animate pop ${description ? 'has-desc' : ''} ${
        animateBorder ? 'has-border' : ''
      }`}
      style={{
        color: color ?? theme.colors.primary,
        borderRadius: radius.box,
        position: 'relative',
        display: 'flex',
        height: 80,
        animationDelay: `${index * 80}ms`,
        cursor: 'pointer',
      }}
    >
      {animateBorder && <StyledLinkBorder />}
      <div
        className="wrapper"
        style={{
          gap: 12,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          zIndex: 2,
          width: '100%',
          height: '100%',
          padding: 20,
          paddingLeft: 30,
          paddingRight: 15,
          borderRadius: radius.box,
        }}
      >
        {icon && (
          <div className="icon" style={{ height: 40, width: 40, zIndex: 2 }}>
            {icon}
          </div>
        )}
        <div
          className="title"
          style={{
            maxWidth: '80%',
            zIndex: 2,
            fontSize: 18,
            fontWeight: 'bold',
            lineHeight: '0.8em',
          }}
        >
          {children}
        </div>
        {description && <div className="description">{description}</div>}
      </div>
    </StyledLink>
  );
};

// () => downloadResource(downloadUrl, downloadName)
export const DownloadDocumentAction = (props: ActionProps) => {
  const theme = useTheme() as unknown as CustomTheme;
  const { downloadFormUrl } = useExperienceConfig();
  let requiresLead = false;
  let formUrl;
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (shouldShowLeadForm(props.publication?.project_id)) {
    requiresLead = true;
    formUrl = downloadFormUrl
      ?.replace('{PROJECT_ID}', props.publication?.project_id ?? '')
      .replace('{ACTION}', 'download');
  }

  return (
    <>
      {requiresLead && formUrl && (
        <LeadFormModal
          formUrl={formUrl}
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
      <OverlayAction
        color={theme.colors.ctaTextColor}
        index={2}
        onClick={() => {
          if (requiresLead) {
            setIsModalOpen(true);
          } else {
            props?.onClick?.();
          }
        }}
        // animateBorder
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              fillRule="evenodd"
              d="M10 22h4c3.771 0 5.657 0 6.828-1.172C22 19.657 22 17.771 22 14v-.437c0-.873 0-1.529-.043-2.063h-4.052c-1.097 0-2.067 0-2.848-.105-.847-.114-1.694-.375-2.385-1.066-.692-.692-.953-1.539-1.067-2.386-.105-.781-.105-1.75-.105-2.848l.01-2.834c0-.083.007-.164.02-.244C11.121 2 10.636 2 10.03 2 6.239 2 4.343 2 3.172 3.172 2 4.343 2 6.229 2 10v4c0 3.771 0 5.657 1.172 6.828C4.343 22 6.229 22 10 22z"
              clipRule="evenodd"
              opacity="0.5"
            ></path>
            <path
              fill="currentColor"
              d="M9.013 19.047a.75.75 0 01-1.026 0l-2-1.875a.75.75 0 011.026-1.094l.737.69V13.5a.75.75 0 011.5 0v3.269l.737-.691a.75.75 0 011.026 1.094l-2 1.875zM11.51 2.26l-.01 2.835c0 1.097 0 2.066.105 2.848.114.847.375 1.694 1.067 2.385.69.691 1.538.953 2.385 1.067.781.105 1.751.105 2.848.105h4.052c.013.155.022.321.028.5H22c0-.268 0-.402-.01-.56a5.322 5.322 0 00-.958-2.641c-.094-.128-.158-.204-.285-.357C19.954 7.494 18.91 6.312 18 5.5c-.81-.724-1.921-1.515-2.89-2.162-.832-.555-1.248-.833-1.819-1.04a5.488 5.488 0 00-.506-.153c-.384-.095-.758-.128-1.285-.14l.01.255z"
            ></path>
          </svg>
        }
      >
        {props.label}
      </OverlayAction>
    </>
  );
};

export const ExternalLinkAction = (props: ActionProps) => {
  const theme = useTheme() as unknown as CustomTheme;

  return (
    <OverlayAction
      color={theme.colors.ctaTextColor}
      index={2}
      onClick={props.onClick}
      // animateBorder
      icon={
        <svg
          data-slot="icon"
          fill="none"
          strokeWidth="1.5"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25"
          ></path>
        </svg>
      }
    >
      {props?.label}
    </OverlayAction>
  );
};

export const PhoneAction = (props: ActionProps) => {
  const theme = useTheme() as unknown as CustomTheme;

  return (
    <OverlayAction
      color={theme.colors.ctaTextColor}
      index={0}
      onClick={props.onClick}
      icon={
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22 7C22 9.76142 19.7614 12 17 12C16.2002 12 15.4442 11.8122 14.7738 11.4783C14.5956 11.3895 14.392 11.36 14.1997 11.4114L13.0867 11.7092C12.6035 11.8385 12.1615 11.3965 12.2908 10.9133L12.5886 9.80031C12.64 9.60803 12.6105 9.4044 12.5217 9.22624C12.1878 8.55582 12 7.79984 12 7C12 4.23858 14.2386 2 17 2C19.7614 2 22 4.23858 22 7ZM17 4.8125C17.5178 4.8125 17.9375 5.23223 17.9375 5.75V6.0625H18.25C18.7678 6.0625 19.1875 6.48223 19.1875 7C19.1875 7.51777 18.7678 7.9375 18.25 7.9375H17.9375V8.25C17.9375 8.76777 17.5178 9.1875 17 9.1875C16.4822 9.1875 16.0625 8.76777 16.0625 8.25V7.9375H15.75C15.2322 7.9375 14.8125 7.51777 14.8125 7C14.8125 6.48223 15.2322 6.0625 15.75 6.0625H16.0625V5.75C16.0625 5.23223 16.4822 4.8125 17 4.8125Z"
            fill={theme.colors.ctaTextColor}
          />
          <path
            opacity="0.5"
            d="M8.03759 7.31617L8.6866 8.4791C9.2723 9.52858 9.03718 10.9053 8.11471 11.8278C8.11471 11.8278 8.11471 11.8278 8.11471 11.8278C8.11459 11.8279 6.99588 12.9468 9.02451 14.9755C11.0525 17.0035 12.1714 15.8861 12.1722 15.8853C12.1722 15.8853 12.1722 15.8853 12.1722 15.8853C13.0947 14.9628 14.4714 14.7277 15.5209 15.3134L16.6838 15.9624C18.2686 16.8468 18.4557 19.0692 17.0628 20.4622C16.2258 21.2992 15.2004 21.9505 14.0669 21.9934C12.1588 22.0658 8.91828 21.5829 5.6677 18.3323C2.41713 15.0817 1.93421 11.8412 2.00655 9.93309C2.04952 8.7996 2.7008 7.77423 3.53781 6.93723C4.93076 5.54428 7.15317 5.73144 8.03759 7.31617Z"
            fill={theme.colors.ctaTextColor}
          />
        </svg>
      }
    >
      {props.label}
    </OverlayAction>
  );
};

export const TakeAppointmentAction = (props: ActionProps) => {
  const theme = useTheme() as unknown as CustomTheme;

  return (
    <OverlayAction
      color={theme.colors.ctaTextColor}
      index={1}
      onClick={props.onClick}
      icon={
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M6.96006 2C7.37758 2 7.71605 2.30996 7.71605 2.69231V4.08883C8.38663 4.07692 9.13829 4.07692 9.98402 4.07692H14.016C14.8617 4.07692 15.6134 4.07692 16.284 4.08883V2.69231C16.284 2.30996 16.6224 2 17.0399 2C17.4575 2 17.7959 2.30996 17.7959 2.69231V4.15008C19.2468 4.25647 20.1992 4.51758 20.899 5.15838C21.5987 5.79917 21.8838 6.67139 22 8V9H2V8C2.11618 6.67139 2.4013 5.79917 3.10104 5.15838C3.80079 4.51758 4.75323 4.25647 6.20406 4.15008V2.69231C6.20406 2.30996 6.54253 2 6.96006 2Z"
            fill={theme.colors.ctaTextColor}
          />
          <path
            opacity="0.5"
            d="M22 14V12C22 11.161 21.9873 9.66527 21.9744 9H2.00586C1.99296 9.66527 2.00564 11.161 2.00564 12V14C2.00564 17.7712 2.00564 19.6569 3.17688 20.8284C4.34813 22 6.23321 22 10.0034 22H14.0023C17.7724 22 19.6575 22 20.8288 20.8284C22 19.6569 22 17.7712 22 14Z"
            fill={theme.colors.ctaTextColor}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16 13.25C16.4142 13.25 16.75 13.5858 16.75 14V15.25L18 15.25C18.4142 15.25 18.75 15.5858 18.75 16C18.75 16.4142 18.4142 16.75 18 16.75H16.75L16.75 18C16.75 18.4142 16.4142 18.75 16 18.75C15.5858 18.75 15.25 18.4142 15.25 18L15.25 16.75L14 16.75C13.5858 16.75 13.25 16.4142 13.25 16C13.25 15.5858 13.5858 15.25 14 15.25H15.25L15.25 14C15.25 13.5858 15.5858 13.25 16 13.25Z"
            fill={theme.colors.ctaTextColor}
          />
        </svg>
      }
    >
      {props.label}
    </OverlayAction>
  );
};

export const EmailAction = (props: ActionProps) => {
  const theme = useTheme() as unknown as CustomTheme;
  const { leadFormUrl } = useExperienceConfig();
  let requiresLead = false;
  let formUrl;
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (shouldShowLeadForm(props.publication?.project_id)) {
    requiresLead = true;
    formUrl = leadFormUrl
      ?.replace('{PROJECT_ID}', props.publication?.project_id ?? '')
      .replace('{ACTION}', 'email');
  }
  return (
    <>
      {requiresLead && formUrl && (
        <LeadFormModal
          formUrl={formUrl}
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
      <OverlayAction
        color={theme.colors.ctaTextColor}
        index={1}
        onClick={() => {
          if (requiresLead) {
            setIsModalOpen(true);
          } else {
            props?.onClick?.();
          }
        }}
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              fill={theme.colors.ctaTextColor}
              fillRule="evenodd"
              d="M6.72 10.6l1.439 1.2c1.836 1.53 2.755 2.295 3.84 2.295 1.087 0 2.005-.765 3.842-2.296l1.44-1.2c.353-.294.53-.442.624-.643.095-.202.095-.432.095-.893V7c0-.32 0-.62-.002-.898-.012-1.771-.098-2.737-.73-3.37C16.535 2 15.357 2 13 2h-2c-2.357 0-3.536 0-4.268.732-.633.633-.72 1.599-.732 3.37-.002.279 0 .577 0 .898v2.063c0 .46 0 .691.094.893.095.201.272.349.625.644zM9.25 6a.75.75 0 01.75-.75h4a.75.75 0 010 1.5h-4A.75.75 0 019.25 6zm1 3a.75.75 0 01.75-.75h2a.75.75 0 010 1.5h-2a.75.75 0 01-.75-.75z"
              clipRule="evenodd"
            ></path>
            <path
              fill={theme.colors.ctaTextColor}
              d="M8.159 11.8l-1.44-1.2c-.353-.295-.53-.442-.625-.644C6 9.754 6 9.524 6 9.064V7 6.102c-1.3.128-2.175.417-2.828 1.07C2 8.343 2 10.23 2 14.002c0 3.77 0 5.656 1.172 6.827 1.171 1.172 3.057 1.172 6.828 1.172h4c3.771 0 5.657 0 6.828-1.172C22 19.659 22 17.773 22 14.002c0-3.771 0-5.658-1.172-6.83-.653-.653-1.529-.942-2.83-1.07.002.28.002.579.002.9v2.063c0 .46 0 .69-.095.892-.094.202-.27.35-.625.644l-1.44 1.2c-1.836 1.53-2.754 2.295-3.84 2.295-1.086 0-2.005-.765-3.841-2.296z"
              opacity="0.5"
            ></path>
          </svg>
        }
      >
        {props.label}
      </OverlayAction>
    </>
  );
};
